import React, { ChangeEvent, useEffect, useState } from 'react';
import { FormCheck, Spinner, Table as BootstrapTable } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SelectedItem, TableProps, TableRow } from '../models/table-models';
import { DeleteActionComponent } from './DeleteActionComponent';
import { EditActionComponent } from './EditActionComponent';
import { checkered } from '../styles/selectStyles';
import { EmailActionComponent } from './EmailActionComponent';
import { CustomButton } from './CustomButton';
import { faSort, faSortDown, faSortUp, faWindowRestore } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Collapse from 'react-bootstrap/Collapse';

export const CustomTable = <T extends TableRow>({
                                                    tableData,
                                                    sendEmailHook,
                                                    idOfInvoiceEmailThatIsLoading,
                                                    disableEmail,
                                                    rowExtraAction,
                                                    sortableColumns,
                                                    sortBy,
                                                    sortDirection,
                                                    onSortChange,
                                                    actionHook,
                                                    disableAction,
                                                    editUsesOwnDialog,
                                                    actionDisabledReason,
                                                    actionInnerHook,
                                                    innerActionComponent,
                                                    actionText,
                                                    actionTextForRow,
                                                    actionIcon,
                                                    hideActionModal,
                                                    deleteButtonText,
                                                    columns,
                                                    editComponent,
                                                    editFooterAction,
                                                    editInnerComponent,
                                                    editInnerTitle,
                                                    editIcon,
                                                    viewComponent,
                                                    viewComponentObject,
                                                    disableRowClick,
                                                    selectable,
                                                    refetchEntitiesHook,
                                                    accessEditRoles,
                                                    accessActionRoles,
                                                    notSelectable,
                                                    setSelectedItems,
                                                    viewAction,
                                                    hasAccordionRow,
                                                    tableFooterText,
                                                    outerTableFooterText,
                                                    comment,
                                                    setComment,
                                                    setDeleteReason,
                                                    hasCalculate,
                                                    isNumbered,
                                                    rowClassName,
                                                    customRows,
                                                    childRows,
                                                    isRowExpanded,
                                                }: TableProps<T>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const [selectedTableItems, setSelectedTableItems] = useState<SelectedItem>({});

    // New
    const [_, setSelectedRow] = useState<number | null>(null);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [selectedInnerRowKeys, setSelectedInnerRowKeys] = useState<string[]>([]);
    const [lastSelectedInnerRowKey, setLastSelectedInnerRowKey] = useState<string>();
    // New End

    const selectAll = (checked: boolean) => {

        const selectedObj = {} as SelectedItem;
        tableData?.forEach((item: any) => {
            if (!notSelectable?.find((i) => i.id === item.id)) selectedObj[item.id] = checked;
        });
        setSelectedTableItems(selectedObj);
    };
    const selectColumn = (id: number) => {

        const updatedSelectedTableItems = {
            ...selectedTableItems,
            [id]: !selectedTableItems[id],
        };
        const selectedItem: any = tableData.find((item) => item.id === id);
        const additionalItems = tableData.filter((item: any) => selectedItem?.id != item.id && selectedItem?.document?.id === item.document?.id);
        additionalItems.forEach((item) => {
            // @ts-ignore
            if (item.document)
                // @ts-ignore
                updatedSelectedTableItems[item.id] = !selectedTableItems[id];
        });


        setSelectedTableItems(updatedSelectedTableItems);
    };

    useEffect(() => {
        if (setSelectedItems) {
            // @ts-ignore
            setSelectedItems(tableData.filter((item: any) => selectedTableItems[item.id]));
        }
    }, [selectedTableItems]);

    useEffect(() => {
        if (setSelectedItems && disableEmail) {
            const selectedObj = {} as SelectedItem;
            tableData?.forEach((item: any) => {
                if (!notSelectable?.find((i) => i.id === item.id)) selectedObj[item.id] = false;
            });
            setSelectedTableItems(selectedObj);
        }
    }, [refetchEntitiesHook]);

    const onAction = async (index: number, hook: (fields: T) => void) => {
        await hook(tableData[index]);
        refetchEntitiesHook && refetchEntitiesHook();
    };

    const toggleTableRow = (index: number) => {
        if (selectedRows.includes(index)) {
            const filteredArray = selectedRows.filter((item) => item !== index);
            setSelectedRows(filteredArray);
            setSelectedRow(null);
            return;
        }
        selectedRows.push(index);
        setSelectedRows(selectedRows);
        setSelectedRow(index);
    };

    const renderTableCells = (item: T, cellIndex: number, fields?: {
        field: string,
        isTranslate?: boolean,
        transform?: (field: string | number | []) => string
    }[]) => {
        if (!fields)
            return Object.keys(item).map((key, index) => (
                <td key={`${cellIndex}-${index}`} className={'p-3'}>
                    {item[key as keyof T]}
                </td>
            ));
        return fields.map((field, index) => {
            const value = field.field.split('.').reduce((acc, key) => {
                return acc && ( acc[key as keyof typeof acc] as any );
            }, item);
            return (
                <td key={`${cellIndex}-${index}`} className={'p-3'}>
                    {field.transform && ( typeof value === 'number' || typeof value === 'string' || Array.isArray(value) ) ? field.transform(value as any) : ( !field.isTranslate || typeof value !== 'string' ? value : t(value) )}
                </td>
            );
        });
    };

    const innerTableData = (colleactionOfData: T[], tableData: T[], index: number) => {
        const filteredArray = colleactionOfData.filter((item) => {
            return columns.groupByField ? columns.groupByField(tableData[index]) === columns.groupByField(item) : true;
        });

        return filteredArray;
    };

    const selectedInnerRowStyle = {
        '--bs-table-bg': '#cfe2ff',
        '--bs-table-accent-bg': '#cfe2ff',
        backgroundColor: '#cfe2ff',
        userSelect: 'none',
    } as React.CSSProperties;

    const innerRowStyle = {
        userSelect: 'none',
    } as React.CSSProperties;

    const getInnerRowSelectionKey = (item: T, fallbackKey: string) => `${item.id ?? fallbackKey}`;

    const isInnerRowSelected = (item: T, fallbackKey: string) => {
        return selectedInnerRowKeys.includes(getInnerRowSelectionKey(item, fallbackKey));
    };

    const toggleInnerRowSelection = (
        item: T,
        innerRows: T[],
        fallbackKey: string,
        event: React.MouseEvent<HTMLTableRowElement>
    ) => {
        const selectionKey = getInnerRowSelectionKey(item, fallbackKey);

        if (event.shiftKey && lastSelectedInnerRowKey != null) {
            const startIndex = innerRows.findIndex((innerItem, index) =>
                getInnerRowSelectionKey(innerItem, `${fallbackKey}-${index}`) === lastSelectedInnerRowKey
            );
            const endIndex = innerRows.findIndex((innerItem, index) =>
                getInnerRowSelectionKey(innerItem, `${fallbackKey}-${index}`) === selectionKey
            );

            if (startIndex >= 0 && endIndex >= 0) {
                const fromIndex = Math.min(startIndex, endIndex);
                const toIndex = Math.max(startIndex, endIndex);
                const rangeKeys = innerRows
                    .slice(fromIndex, toIndex + 1)
                    .map((innerItem, index) =>
                        getInnerRowSelectionKey(innerItem, `${fallbackKey}-${fromIndex + index}`)
                    );

                setSelectedInnerRowKeys((current) => Array.from(new Set([...current, ...rangeKeys])));
                setLastSelectedInnerRowKey(selectionKey);
                return;
            }
        }

        setSelectedInnerRowKeys((current) =>
            current.includes(selectionKey)
                ? current.filter((key) => key !== selectionKey)
                : [...current, selectionKey]
        );
        setLastSelectedInnerRowKey(selectionKey);
    };

    const getSelectedInnerRows = (innerRows: T[], parentIndex: number) => {
        return innerRows.filter((innerItem, index) =>
            selectedInnerRowKeys.includes(getInnerRowSelectionKey(innerItem, `${parentIndex}-${index}`))
        );
    };

    const toggleAllInnerRowsSelection = (innerRows: T[], parentIndex: number, checked: boolean) => {
        const rowKeys = innerRows.map((innerItem, index) => getInnerRowSelectionKey(innerItem, `${parentIndex}-${index}`));

        setSelectedInnerRowKeys((current) =>
            checked
                ? Array.from(new Set([...current, ...rowKeys]))
                : current.filter((key) => !rowKeys.includes(key))
        );
        setLastSelectedInnerRowKey(checked ? rowKeys.at(-1) : undefined);
    };

    function calculateColumnSums<T extends Record<string, any>>(data: T[]): Record<string, number> {
        if (hasCalculate === false) return {};
        const columnSums: Record<string, number> = {};

        data.forEach((item) => {
            const processItem = (obj: Record<string, any>) => {
                Object.entries(obj).forEach(([key, value]) => {
                    if (typeof value === 'number' && key != 'id') {
                        // Add the value to the corresponding column sum
                        columnSums[key] = ( columnSums[key] || 0 ) + value;
                    } else if (typeof value === 'object' && value !== null) {
                        // Recursively process the object
                        processItem(value);
                    }
                });
            };
            processItem(item);
        });

        Object.keys(columnSums).forEach((key) => {
            columnSums[key] = Math.round(columnSums[key] * 100) / 100;
        });

        return columnSums;
    }

    const sums = calculateColumnSums(tableData);
    const renderCustomRow = (row: NonNullable<TableProps<T>['customRows']>[number], index: number, rowIndex: number) => (
        <tr
            key={`custom-row-${index}`}
            className={`${row.onClick ? 'hovered-row cursor-pointer' : ''} ${checkered(rowIndex)} ${row.className ?? ''}`}
            onClick={row.onClick}
        >
            {isNumbered && <td className='align-middle'>{rowIndex + 1}</td>}
            {selectable && <td className='align-middle' />}
            {columns.headings.map((_, cellIndex) => (
                <td key={cellIndex} className='align-middle'>{row.cells[cellIndex] ?? ''}</td>
            ))}
            {( editComponent || actionHook || sendEmailHook || viewAction || rowExtraAction ) && (
                <td className='align-middle border-none'>
                    <div className='d-flex justify-content-center align-items-center'>
                        <span className='invisible d-inline-block' style={{ width: '32px', height: '32px' }} />
                    </div>
                </td>
            )}
        </tr>
    );

    return tableData && tableData.length > 0 ? (
        <div className='w-100 rounded '>
            <BootstrapTable responsive>
                <thead className='background-dark color-main'>
                <tr>
                    {
                        isNumbered && <th className='single-line-text table-header text-center fw-bold'>{t('no')}</th>
                    }
                    {selectable && (
                        <th className='border-radius-default-left'>
                            <FormCheck
                                checked={
                                    Object.values(selectedTableItems).filter((item) => item).length ===
                                    tableData.length - ( notSelectable?.length ?? 0 ) &&
                                    Object.values(selectedTableItems).filter((item) => item).length > 0
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) => selectAll(e.target.checked)}
                                type='checkbox'
                            />
                        </th>
                    )}
                    {columns.headings.map((column, index) => {
                        // Подредбата е серверна, защото списъкът е разбит на страници - подреждане само
                        // на текущата страница би подредило двайсет реда от сто хиляди.
                        const sortable = onSortChange !== undefined && sortableColumns?.includes(column);
                        const sortedByThis = sortable && sortBy === column;
                        return (
                            <th
                                key={index}
                                className={`single-line-text table-header text-center fw-bold${sortable ? ' sortable-header' : ''}`}
                                role={sortable ? 'button' : undefined}
                                onClick={sortable ? () => onSortChange!(column) : undefined}
                            >
                                {t(`${column}`)}
                                {sortable && (
                                    <FontAwesomeIcon
                                        icon={sortedByThis ? (sortDirection === 'ASC' ? faSortUp : faSortDown) : faSort}
                                        className={`ms-1${sortedByThis ? '' : ' opacity-50'}`}
                                    />
                                )}
                            </th>
                        );
                    })}
                    {( editComponent || actionHook || viewAction ) && (
                        <th className='text-center fw-bold'>{t(`actions`)}</th>
                    )}
                </tr>
                </thead>

                <tbody className={`${hasAccordionRow ? 'accordionTableRow cursor-pointer' : ''} `}>
                {columns.items.map((item, index) => (
                    <React.Fragment key={index}>
                        {customRows
                            ?.filter((row) => row.insertIndex === index)
                            .map((row, customRowIndex) => renderCustomRow(row, customRowIndex, index + customRowIndex))}
                        <tr className={`${(viewComponent || viewComponentObject) && !disableRowClick?.(tableData[index]) ? 'hovered-row' : ''} ${checkered(index)} ${rowClassName?.(tableData[index], index) ?? ''}`}>
                            {
                                isNumbered && <td>{index + 1}</td>
                            }
                            {selectable && (
                                <td>
                                    <FormCheck
                                        onChange={() => {

                                            const id = tableData[index].id ?? 0;

                                            selectColumn(id);
                                        }}
                                        disabled={!!notSelectable?.find((i) => i.id === tableData[index].id)}
                                        checked={selectedTableItems[tableData[index].id ?? 0] ?? false}
                                        type='checkbox'
                                    />
                                </td>
                            )}

                            {item.map((columnData, sIndex) =>
                                typeof columnData === 'boolean' ? (
                                    <td key={sIndex}>
                                        <input
                                            type='checkbox'
                                            id='checkbox'
                                            checked={columnData}
                                            onChange={() => {}}
                                        />
                                    </td>
                                ) : (
                                    <td
                                        onClick={disableRowClick?.(tableData[index]) ? undefined : () => {
                                            const id = tableData[index].id ?? 0;
                                            if (viewComponentObject) {
                                                viewComponentObject(tableData[index]);
                                            } else if (viewComponent && !viewAction) {
                                                viewComponent(id.toString());
                                            } else if (hasAccordionRow) {
                                                toggleTableRow(index);
                                            }
                                        }}
                                        key={sIndex}
                                    >
                                        {Array.isArray(columnData) ? columnData.join(' ') : columnData}
                                    </td>
                                ),
                            )}
                            {( editComponent || actionHook || sendEmailHook || viewAction || rowExtraAction ) && (
                                <td className='align-middle border-none'>
                                    <div className='d-flex justify-content-center'>
                                        {tableData[index].id &&
                                            sendEmailHook &&
                                            ( idOfInvoiceEmailThatIsLoading === tableData[index].id ? (
                                                <Spinner animation={'border'} />
                                            ) : (
                                                <EmailActionComponent
                                                    onAction={() => onAction(index, sendEmailHook)}
                                                    disabled={disableEmail && disableEmail(tableData[index])}
                                                    hasEmailError={Boolean((tableData[index] as TableRow)?.emailError)}
                                                    {...{ accessActionRoles }}
                                                />
                                            ) )}
                                        {rowExtraAction && rowExtraAction(tableData[index])}
                                        {viewAction && (
                                            <CustomButton
                                                icon={faWindowRestore}
                                                iconClass='fa-fw'
                                                onClick={() => {
                                                    const id = tableData[index].id ?? 0;

                                                    if (viewComponent) {
                                                        viewComponent(id.toString());
                                                    }
                                                }}
                                                className='mx-1 color-main'
                                                // disabled={accessEditRoles && !hasPermission([...accessEditRoles, 'ADMIN'])}
                                            />
                                        )}
                                        {editComponent && refetchEntitiesHook && (
                                            <EditActionComponent
                                                editComponent={editComponent}
                                                ownDialog={editUsesOwnDialog?.(tableData[index])}
                                                title={t('edit')}
                                                item={tableData[index]}
                                                editIcon={editIcon}
                                                editFooterAction={editFooterAction}
                                                {...{ refetchEntitiesHook, accessEditRoles }}
                                            ></EditActionComponent>
                                        )}
                                        {tableData[index].id && actionHook && (
                                            <DeleteActionComponent
                                                onAction={() => onAction(index, actionHook)}
                                                disabled={disableAction?.(tableData[index])}
                                                disabledReason={actionDisabledReason?.(tableData[index])}
                                                actionText={actionText}
                                                translatedActionText={actionTextForRow?.(tableData[index])}
                                                actionIcon={actionIcon}
                                                hideModal={hideActionModal}
                                                comment={comment}
                                                setComment={setComment}
                                                {...{ deleteButtonText, accessActionRoles }}
                                            />
                                        )}
                                    </div>
                                </td>
                            )}
                        </tr>
                        {/* Подредовете на обединената фактура: отделните ѝ фактури под общия ред. */}
                        {isRowExpanded?.(tableData[index], index) &&
                            childRows?.(tableData[index], index)?.map((childCells, childIndex) => (
                                <tr key={`child-${index}-${childIndex}`} className='bg-light small'>
                                    {isNumbered && <td />}
                                    {selectable && <td />}
                                    {childCells.map((cell, cellIndex) => (
                                        <td key={cellIndex} className='align-middle'>{cell}</td>
                                    ))}
                                    {( editComponent || actionHook || sendEmailHook || viewAction || rowExtraAction ) && <td />}
                                </tr>
                            ))}
                        {hasAccordionRow && columns.collectionOfItems && columns.collectionOfItems.length > 0 && (
                            <Collapse in={selectedRows.includes(index)}>
                                <tr className=''>

                                    <td colSpan={columns.headings.length + ( selectable ? 2 : 1 )} className='p-2'>
	                                        {/* <Collapse in={selectedRows.includes(index)}> */}
	                                        <div className=''>
                                                {innerActionComponent && (() => {
                                                    const innerRows = innerTableData(
                                                        columns.collectionOfItems,
                                                        tableData,
                                                        index,
                                                    );
                                                    const selectedInnerRows = getSelectedInnerRows(innerRows, index);

                                                    return (
                                                        <div className='d-flex justify-content-start mb-2'>
                                                            <FormCheck
                                                                className='d-flex align-items-center gap-2 mb-0'
                                                                checked={innerRows.length > 0 && selectedInnerRows.length === innerRows.length}
                                                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                                    toggleAllInnerRowsSelection(innerRows, index, event.target.checked)
                                                                }
                                                                label={t('markEntireQuantity')}
                                                                type='checkbox'
                                                            />
                                                        </div>
                                                    );
                                                })()}
	                                            <table className='w-100'>
                                                <thead className='background-dark color-main'>
                                                <tr className='m-2 border border-bottom-1 border'>
                                                    {columns?.collapsedHeadings?.map((column, index) => {
                                                        return (
                                                            <th
                                                                key={index}
                                                                className='single-line-text table-header text-center fw-bold p-2'
                                                            >
                                                                {t(`${column}`)}
                                                            </th>
                                                        );
                                                    })}
                                                    {( editInnerComponent || actionInnerHook || innerActionComponent ) && (
                                                        <th className='single-line-text table-header text-center fw-bold p-2'>
                                                            {t(`actions`)}
                                                        </th>
                                                    )}
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {(() => {
                                                    const innerRows = innerTableData(
                                                        columns.collectionOfItems,
                                                        tableData,
                                                        index,
                                                    );
                                                    const selectedInnerRows = getSelectedInnerRows(innerRows, index);

                                                    return innerRows.map((value, cellIndex) => {
                                                        const fallbackKey = `${index}-${cellIndex}`;
                                                        const isSelected = isInnerRowSelected(value, fallbackKey);

                                                        return (
                                                            <tr
                                                                key={cellIndex}
                                                                className={`${isSelected ? '' : checkered(cellIndex)} ${innerActionComponent ? 'cursor-pointer' : ''}`}
                                                                style={isSelected ? selectedInnerRowStyle : innerRowStyle}
                                                                onClick={(event) => {
                                                                    if (innerActionComponent) {
                                                                        toggleInnerRowSelection(value, innerRows, fallbackKey, event);
                                                                    }
                                                                }}
                                                            >
                                                                {renderTableCells(
                                                                    value,
                                                                    cellIndex,
                                                                    columns.innerTableFields,
                                                                )}
                                                                <td
                                                                    className='align-middle border-none'
                                                                    onClick={(event) => event.stopPropagation()}
                                                                >
                                                                    <div className='d-flex justify-content-center gap-1'>
                                                                        {editInnerComponent && refetchEntitiesHook && (
                                                                            <EditActionComponent
                                                                                editComponent={editInnerComponent}
                                                                                title={editInnerTitle ?? t('edit')}
                                                                                item={value}
                                                                                editIcon={editIcon}
                                                                                disabled={'trackingType' in value && value.trackingType === 'LIQUID_ML'}
                                                                                {...{ refetchEntitiesHook, accessEditRoles }}
                                                                            ></EditActionComponent>
                                                                        )}
                                                                        {innerActionComponent && refetchEntitiesHook && innerActionComponent({
                                                                            item: value,
                                                                            selectedItems: selectedInnerRows,
                                                                            refetchEntitiesHook,
                                                                        })}
                                                                        {actionInnerHook && (
                                                                            <DeleteActionComponent
                                                                                onAction={() => actionInnerHook(value)}
                                                                                actionText={actionText}
                                                                                actionIcon={actionIcon}
                                                                                hideModal={hideActionModal}
                                                                                comment={comment}
                                                                                setComment={setComment}
                                                                                setDeleteReason={setDeleteReason}
                                                                                {...{ deleteButtonText, accessActionRoles }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    });
                                                })()}
                                                </tbody>
                                            </table>
                                            {/* tableFooterText && */}
                                            {columns.collectionOfItems && columns.collectionOfItems.length && (
                                                <div className='d-flex justify-content-end mt-2'>
                                                    <p className='fw-bold text-right'>
                                                        {tableFooterText && tableFooterText(tableData[index])}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        {/* </Collapse> */}
                                    </td>
                                </tr>
                            </Collapse>
                        )}
                    </React.Fragment>
                ))}
                {customRows
                    ?.filter((row) => row.insertIndex === undefined || row.insertIndex >= columns.items.length)
                    .map((row, index) => renderCustomRow(row, index, columns.items.length + index))}
                {hasCalculate && (
                    <tr style={{ fontWeight: 'bold' }}>
                        {isNumbered && <td></td>}
                        {columns.headings.map((column, index) => (
                            <td key={index}>
                                {Object.keys(sums).includes(column) ? t('total') + ': ' + sums[column] : '-'}
                            </td>
                        ))}
                    </tr>
                )}
                </tbody>
            </BootstrapTable>
            {outerTableFooterText && (
                <div className='mt-2'>
                    {typeof outerTableFooterText === 'string' ? (
                        <p className='fw-bold text-right'>{outerTableFooterText}</p>
                    ) : (
                        outerTableFooterText
                    )}
                </div>
            )}
        </div>
    ) : (
        <p> No Data </p>
    );
};
